import { useCallback, useMemo, useState } from 'react';
import styled, { css } from 'styled-components';

import Button from 'components/shared/Button';
import { Select } from 'components/shared/form/InfiniteScrollDropdown';
import useWorkflowPermissions from 'hooks/useWorkflowPermissions';
import { simpleDate } from 'lib/date';
import {
  BUCKET_RENDER_ORDER,
  MATCH_BUCKET,
  MatchBucket,
  MatchReviews,
  OffenderMatch,
  OffenderOffense,
  OffenderSource,
  REVIEW_DECISION,
  ReviewDecision,
  SexOffenderCheckAction,
} from 'models/SexOffenderCheckAction';
import Workflow from 'models/Workflow';
import { WorkflowValuesInput } from 'services/api/workflows';
import { colors } from 'styles/theme/colors';
import { BodySmall, BodySmallBold } from 'styles/typography';
import DenialListEmpty from 'svg/DenialListEmpty';
import LinkIcon from 'svg/LinkIcon';
import MatchCheckIcon from 'svg/MatchCheckIcon';

import DetailSlideout from './DetailSlideout';
import fallbackImageUrl from './SexOffenderCheckFallback.png';

// The one-line summary shown in the SexOffenderCheck row body. In a multi-action category the row
// itself is the clickable slideout trigger; this summary is plain text, no box, no chevron.
//
// Five states, first match wins. Mirrors the server-side effective_status resolver so the copy
// stays consistent with the flagged/complete treatment the referral page applies:
//   1. No matches (isMatch false) → "No matches were found…" — driver returned zero candidates.
//   2. Confirmed match(es) → "A matched sex offender was selected…" — user affirmed a real hit.
//   3. Any strong/plausible undismissed → "N possible matches found out of M results" — the
//      attention-signal copy from the design; N is the count the reviewer actually cares about.
//   4. Every match dismissed → "All N results were dismissed." — reviewer cleared everything;
//      distinguishes decisive reviewer action from noise-only auto-complete (case 5).
//   5. Only unlikely / very_unlikely undismissed → "Only unlikely or very unlikely results remain."
//      Covers two paths that land here: pre-action driver-returned-only-noise, and post-action
//      "reviewer dismissed all the possible matches, unlikely/very_unlikely still linger." Same
//      copy either way — the state is the same "nothing needs review" from here.
export function SexOffenderCheckSummary({ action }: { action: SexOffenderCheckAction }) {
  const total = action.matches.length;
  if (!action.isMatch) {
    return (
      <SummaryText data-testid='sex-offender-check-result'>
        No matches were found in federal or state sex-offender registries.
      </SummaryText>
    );
  }

  const confirmedCount = action.matchedMatches.length;
  if (confirmedCount > 0) {
    const noun = confirmedCount === 1 ? 'sex offender was' : 'sex offenders were';
    return (
      <SummaryText data-testid='sex-offender-check-flag'>
        {confirmedCount === 1 ? 'A' : confirmedCount} matched {noun} selected from {total} total{' '}
        {total === 1 ? 'result' : 'results'}.
      </SummaryText>
    );
  }

  const undismissedPossibleCount = action.strongOrPlausibleMatches.filter(
    (m) => action.reviewFor(m.uuid)?.decision !== REVIEW_DECISION.DISMISS
  ).length;
  if (undismissedPossibleCount > 0) {
    // Verb tenses split on adjudication progress: "found" is the initial reading of the driver's
    // output; once the reviewer has recorded any decision the copy shifts to "remaining" to
    // reflect work-in-progress (some dismissals may have already trimmed the number).
    const verb = action.hasAnyReview ? 'remaining' : 'found';
    return (
      <SummaryText data-testid='sex-offender-check-flag'>
        {undismissedPossibleCount} possible {undismissedPossibleCount === 1 ? 'match' : 'matches'} {verb} out of {total}{' '}
        {total === 1 ? 'result' : 'results'}.
      </SummaryText>
    );
  }

  if (action.dismissedMatches.length === total) {
    return (
      <SummaryText data-testid='sex-offender-check-result'>
        All {total} {total === 1 ? 'result was' : 'results were'} dismissed.
      </SummaryText>
    );
  }

  return (
    <SummaryText data-testid='sex-offender-check-result'>Only unlikely or very unlikely results remain.</SummaryText>
  );
}

// The SexOffenderCheck detail slideout. Rendered on `completed` — the integration always
// completes the action once it has a terminal result (matches or clear), mirroring DenialCheck's
// autonomous-completion model. Per-match Dismiss/Match decisions patch
// `action.outputs['sex_offender_match_reviews']` via saveWorkflowActionProgress — no lifecycle
// transition, no first-vs-subsequent-decision distinction. Empty state renders on `clear`.
export default function SexOffenderCheckReview({
  action,
  workflow,
  open,
  onClose,
  onSaveProgress,
  isSavingProgress,
}: {
  action: SexOffenderCheckAction;
  workflow: Workflow;
  open: boolean;
  onClose: () => void;
  onSaveProgress: (actionId: string, outputs: WorkflowValuesInput, options?: { narrationHint?: string }) => void;
  isSavingProgress: boolean;
}) {
  const checkedName = action.checkedName;
  const matches = action.matches;
  const busy = isSavingProgress;
  const { canEditAction } = useWorkflowPermissions();
  // Every adjudication here is a save_progress write the API's policy refuses for a viewer, so the buttons come off
  // rather than 403-ing on click. The slideout itself stays open: the matches, their demographics, crimes and sources
  // are the check's result, and reading the result is the whole point of a view-only account. Resolved per ACTION
  // rather than from workflow-level edit, so a specialist holding only this kind's grant still adjudicates.
  const canEdit = canEditAction(action, workflow);

  // Stable reference so the hook's memoized derivations (dismissedCount, visible) don't
  // needlessly recompute on unrelated parent renders.
  const reviewFor = useCallback((uuid: string) => action.reviewFor(uuid)?.decision, [action]);

  // Every per-match decision (Dismiss OR Match, first or Nth) is a save_progress write —
  // patches `action.outputs['sex_offender_match_reviews']` on an already-completed action.
  //
  // The write MUST carry the current screen_result alongside the reviews: Rails' record_outputs
  // does `update!(outputs:)` whole-hog (workflow_action.rb:124), not a merge — omitting
  // screen_result would wipe it off action.outputs and the FE model (which reads from
  // action.outputs) would flip to the empty "No matches found" state on refresh.
  const applyDecision = (uuid: string, next: ReviewDecision | null) => {
    if (busy) return;
    const nextReviews = mergeReview(action.reviews, uuid, next);
    onSaveProgress(action.id, {
      sex_offender_screen_result: action.screenResult,
      sex_offender_match_reviews: nextReviews,
    });
  };

  // Batch dismissal for a bucket's "Dismiss All" affordance. One save_progress round-trip carrying
  // every uuid flipped to dismiss — Rails' record_outputs accepts the whole array atomically, so
  // there's no partial-apply race. The `bucket` param is threaded through to the server as a
  // narrationHint so the activity-log entry reads "Dismissed all Strong results" (etc.) rather
  // than the per-card verb the server-side diff alone would produce.
  const applyBatchDismiss = (uuids: string[], bucket: MatchBucket) => {
    if (busy || uuids.length === 0) return;
    const nextReviews = uuids.reduce<MatchReviews>(
      (acc, uuid) => mergeReview(acc, uuid, REVIEW_DECISION.DISMISS),
      action.reviews
    );
    onSaveProgress(
      action.id,
      {
        sex_offender_screen_result: action.screenResult,
        sex_offender_match_reviews: nextReviews,
      },
      { narrationHint: bucket }
    );
  };

  // Filter/dismiss state + derivations live at the panel level so the filter bar can sit in the
  // shell's detailsBar (above the scrolling body, outside it) while the cards inside the body
  // still see the same filtered `visible` list. Hoisting also lets the count in the filter
  // heading stay wired to the same visible[] the cards render.
  const listState = useMatchListState(matches);

  return (
    <DetailSlideout
      open={open}
      onClose={onClose}
      title={`${workflow.patientName}: Sex Offender Check`}
      subtitle='Checking against federal and state-based sex offender registries to find any matches to this patient’s demographics'
      detailsBar={
        action.isMatch ? (
          <MatchFilters
            visibleCount={listState.filtered.length}
            checkedName={checkedName}
            options={listState.options}
            filters={listState.filters}
            setFilters={listState.setFilters}
          />
        ) : undefined
      }>
      {action.isMatch ? (
        <MatchCards
          filteredByBucket={listState.filteredByBucket}
          expandedBuckets={listState.expandedBuckets}
          toggleExpandedBucket={listState.toggleExpandedBucket}
          busy={busy}
          canEdit={canEdit}
          reviewFor={reviewFor}
          applyDecision={applyDecision}
          applyBatchDismiss={applyBatchDismiss}
        />
      ) : (
        <EmptyState data-testid='sex-offender-check-no-matches'>
          <DenialListEmpty width={40} height={40} />
          <EmptyTitle>No Matches Found for ‘{checkedName}’</EmptyTitle>
          <EmptyBody>
            Olio did not identify this patient in any federal or state-based sex-offender registries.
          </EmptyBody>
        </EmptyState>
      )}
    </DetailSlideout>
  );
}

// Return `reviews` with the entry for `uuid` set to `next` (or removed when next is null so
// the caller can undo a decision back to the un-adjudicated state). Never mutates the input.
// Preserves the order of untouched entries and appends new ones — stable for the FE render.
function mergeReview(reviews: MatchReviews, uuid: string, next: ReviewDecision | null): MatchReviews {
  const filtered = reviews.filter((r) => r.uuid !== uuid);
  if (next === null) return filtered;
  return [...filtered, { uuid, decision: next }];
}

// Client-side filter controls layered over the loaded matches[]. Every dropdown is populated
// from the actual values present in the list (so the reviewer only picks from real options),
// with "All" as the neutral default. Dismissed cards keep their place — visually greyed by
// CardStyle — so the reviewer can still see what was decided. Sort order is fixed: highest
// match_score first, computed at driver time by the integration's scoring module. See
// olio-integration app/apis/offendercheck/scoring.py + OFFENDERS_IO_NOTES §5 for the design.
type FilterKey = 'state' | 'sex' | 'race' | 'hairColor' | 'eyeColor';

const FILTERS: { key: FilterKey; label: string }[] = [
  { key: 'state', label: 'State' },
  { key: 'sex', label: 'Sex' },
  { key: 'race', label: 'Race' },
  { key: 'hairColor', label: 'Hair Color' },
  { key: 'eyeColor', label: 'Eye Color' },
];

// Reviewer-facing display name for each bucket. Used in section headers ("2 'Strong' Matches").
const BUCKET_DISPLAY: Record<MatchBucket, string> = {
  [MATCH_BUCKET.STRONG]: 'Strong',
  [MATCH_BUCKET.PLAUSIBLE]: 'Plausible',
  [MATCH_BUCKET.UNLIKELY]: 'Unlikely',
  [MATCH_BUCKET.VERY_UNLIKELY]: 'Very Unlikely',
};

// A match with a missing/unknown bucket string gets grouped under strong — same safety default
// the server-side resolver uses (see SexOffenderCheck#strong_or_plausible_undismissed?).
function bucketOf(match: OffenderMatch): MatchBucket {
  const raw = (match.matchBucket ?? '').trim();
  return raw === '' ? MATCH_BUCKET.STRONG : (raw as MatchBucket);
}

type FilterOption = { value: string; label: string };
type MatchListOptions = Record<FilterKey, FilterOption[]>;

// Sentinel filter value that groups every "we don't know" variant into one option:
// missing/null field, empty string, or a literal "Unknown" (offenders.io actually returns the
// word in some feeds). Kept as an internal token so it can't collide with a real registry value
// once we lowercase everything.
const UNKNOWN_VALUE = '__unknown__';
const UNKNOWN_LABEL = 'Unknown';

function isUnknownValue(v: unknown): boolean {
  if (typeof v !== 'string') return true;
  const trimmed = v.trim();
  return trimmed === '' || trimmed.toLowerCase() === 'unknown';
}

// Shared state + derivations for the filter bar (rendered between PanelHeader and PanelBody)
// and the cards list (rendered inside PanelBody). Lifted to the panel-level component so both
// siblings see the same filtered visible[] and per-bucket dismissed toggle state.
function useMatchListState(matches: OffenderMatch[]) {
  const [filters, setFilters] = useState<Record<FilterKey, string>>({
    state: '',
    sex: '',
    race: '',
    hairColor: '',
    eyeColor: '',
  });
  // Dismissed cards are HIDDEN by default; each bucket section carries its own reveal state so
  // the reviewer can expand "Show Dismissed" in one bucket without unhiding another.
  const [expandedBuckets, setExpandedBuckets] = useState<Set<MatchBucket>>(() => new Set());
  const toggleExpandedBucket = useCallback((bucket: MatchBucket) => {
    setExpandedBuckets((prev) => {
      const next = new Set(prev);
      if (next.has(bucket)) next.delete(bucket);
      else next.add(bucket);
      return next;
    });
  }, []);

  // Options are deduped case-insensitively (offenders.io mixes "WHITE" and "White" across state
  // feeds — we only want one entry per value). `value` is the lowercased key that both dropdown
  // and match filter compare against; `label` is the Title-Case display. Short all-uppercase
  // tokens (≤ 2 chars, e.g. state codes "WA", sex "M") stay as-is instead of getting title-cased.
  // An "Unknown" option is appended when any record in the field is missing/empty or literally
  // "Unknown", so the reviewer can single those out — matches passesFilters via UNKNOWN_VALUE.
  const options: MatchListOptions = useMemo(() => {
    const collect = (key: FilterKey): FilterOption[] => {
      const seen = new Map<string, string>();
      let hasUnknown = false;
      matches.forEach((m) => {
        const v = m[key];
        if (isUnknownValue(v)) {
          hasUnknown = true;
          return;
        }
        const trimmed = (v as string).trim();
        const lower = trimmed.toLowerCase();
        if (!seen.has(lower)) seen.set(lower, displayFilterValue(trimmed));
      });
      const known = Array.from(seen.entries())
        .sort(([, a], [, b]) => a.localeCompare(b))
        .map(([value, label]) => ({ value, label }));
      return hasUnknown ? [...known, { value: UNKNOWN_VALUE, label: UNKNOWN_LABEL }] : known;
    };
    return {
      state: collect('state'),
      sex: collect('sex'),
      race: collect('race'),
      hairColor: collect('hairColor'),
      eyeColor: collect('eyeColor'),
    };
  }, [matches]);

  // Filter-passing matches, kept in the driver's pre-sorted order (combined match_score DESC).
  // Dismissed matches are INCLUDED here — the per-section renderer decides whether to hide them
  // behind the "View Dismissed Result" toggle, so the header count matches the section behavior.
  const filtered = useMemo(() => {
    const passesFilters = (m: OffenderMatch) =>
      FILTERS.every((f) => {
        const selected = filters[f.key];
        if (!selected) return true;
        const v = m[f.key];
        if (selected === UNKNOWN_VALUE) return isUnknownValue(v);
        return typeof v === 'string' && v.trim().toLowerCase() === selected;
      });
    return matches.filter(passesFilters);
  }, [matches, filters]);

  // Filtered matches split into buckets in reviewer-facing render order. A section with an empty
  // array is fine — MatchCards skips it. This preserves the driver's DESC sort within each
  // bucket because the split is order-preserving.
  const filteredByBucket = useMemo(() => {
    const grouped: Record<MatchBucket, OffenderMatch[]> = {
      [MATCH_BUCKET.STRONG]: [],
      [MATCH_BUCKET.PLAUSIBLE]: [],
      [MATCH_BUCKET.UNLIKELY]: [],
      [MATCH_BUCKET.VERY_UNLIKELY]: [],
    };
    filtered.forEach((m) => {
      const b = bucketOf(m);
      if (grouped[b]) grouped[b].push(m);
    });
    return grouped;
  }, [filtered]);

  return {
    filters,
    setFilters,
    expandedBuckets,
    toggleExpandedBucket,
    options,
    filtered,
    filteredByBucket,
  };
}

// The filter bar — heading + five dropdowns — rendered as a sibling of PanelHeader and
// PanelBody so it sits between them without scrolling with the cards below.
function MatchFilters({
  visibleCount,
  checkedName,
  options,
  filters,
  setFilters,
}: {
  visibleCount: number;
  checkedName: string;
  options: MatchListOptions;
  filters: Record<FilterKey, string>;
  setFilters: React.Dispatch<React.SetStateAction<Record<FilterKey, string>>>;
}) {
  type OptionShape = { label: string; value: string };
  return (
    <FilterBar>
      <ListHeaderRow>
        <MatchesHeader data-testid='sex-offender-matches-heading'>
          {/* Heading shows the filter-adjusted count, not the total — matches the Figma redesign
              (OLIO-2391 v2). When no filters are active, visibleCount === totalCount naturally,
              so the header reads "25 matches found for 'Ismael Dunn'"; when filtered down to a
              single record it reads "1 match found for 'Ismael Dunn'". */}
          {visibleCount} {visibleCount === 1 ? 'match' : 'matches'} found for ‘{checkedName}’
        </MatchesHeader>
      </ListHeaderRow>
      <FiltersRow>
        {FILTERS.map((f) => {
          const opts: OptionShape[] = [{ label: 'All', value: '' }, ...options[f.key]];
          return (
            <FilterCell key={f.key}>
              <Select<OptionShape>
                aria-label={f.label}
                options={opts}
                value={opts.find((o) => o.value === filters[f.key]) ?? opts[0]}
                onChange={(o) => setFilters((prev) => ({ ...prev, [f.key]: o.value }))}
                getOptionValue={(o) => o.value}
                getOptionLabel={(o) => o.label}
                formatOptionLabel={(o, meta) =>
                  meta?.context === 'value' ? twoTonePillLabel(f.label, o.label) : o.label
                }
                isSearchable={false}
                styles={FILTER_PILL_STYLES}
              />
            </FilterCell>
          );
        })}
      </FiltersRow>
    </FilterBar>
  );
}

// The scrolling body: one section per bucket that has ANY match after filter. Dismissed
// candidates hide by default within their section behind a "View Dismissed Result" toggle so
// the reviewer can dispose of noise without losing the ability to revisit it.
function MatchCards({
  filteredByBucket,
  expandedBuckets,
  toggleExpandedBucket,
  busy,
  canEdit,
  reviewFor,
  applyDecision,
  applyBatchDismiss,
}: {
  filteredByBucket: Record<MatchBucket, OffenderMatch[]>;
  expandedBuckets: Set<MatchBucket>;
  toggleExpandedBucket: (bucket: MatchBucket) => void;
  busy: boolean;
  canEdit: boolean;
  reviewFor: (uuid: string) => ReviewDecision | undefined;
  applyDecision: (uuid: string, next: ReviewDecision | null) => void;
  applyBatchDismiss: (uuids: string[], bucket: MatchBucket) => void;
}) {
  const totalVisible = BUCKET_RENDER_ORDER.reduce((sum, b) => sum + filteredByBucket[b].length, 0);
  if (totalVisible === 0) {
    return <NoResultsRow>No matches meet these filters.</NoResultsRow>;
  }

  return (
    <>
      {BUCKET_RENDER_ORDER.map((bucket) => {
        const inBucket = filteredByBucket[bucket];
        if (inBucket.length === 0) return null;
        const undismissed = inBucket.filter((m) => reviewFor(m.uuid) !== REVIEW_DECISION.DISMISS);
        const dismissed = inBucket.filter((m) => reviewFor(m.uuid) === REVIEW_DECISION.DISMISS);
        const expanded = expandedBuckets.has(bucket);
        const sectionSize = inBucket.length;

        return (
          <BucketSection key={bucket} data-testid={`sex-offender-bucket-${bucket}`}>
            <BucketHeader>
              <BucketHeaderLabel>
                {sectionSize} {BUCKET_DISPLAY[bucket]} {sectionSize === 1 ? 'Match' : 'Matches'}
              </BucketHeaderLabel>
              {/* Dismiss All only shows when there's anything left to dismiss in this section.
                  Batches into a single save_progress via applyBatchDismiss — see the parent's
                  onSaveProgress wiring. */}
              {undismissed.length > 0 && canEdit && (
                <DismissAllLink
                  type='button'
                  disabled={busy}
                  onClick={() =>
                    applyBatchDismiss(
                      undismissed.map((m) => m.uuid),
                      bucket
                    )
                  }
                  data-testid={`sex-offender-dismiss-all-${bucket}`}>
                  Dismiss All
                </DismissAllLink>
              )}
            </BucketHeader>

            {dismissed.length > 0 && (
              <DismissedToggleRow>
                <ViewDismissedButton
                  type='button'
                  variant='outline-primary'
                  onClick={() => toggleExpandedBucket(bucket)}
                  data-testid={`sex-offender-toggle-dismissed-${bucket}`}>
                  {expanded
                    ? `Hide Dismissed (${dismissed.length})`
                    : `View Dismissed Result${dismissed.length === 1 ? '' : 's'} (${dismissed.length})`}
                </ViewDismissedButton>
              </DismissedToggleRow>
            )}

            <Cards>
              {undismissed.map((match) => (
                <MatchCard
                  key={match.uuid}
                  match={match}
                  decision={reviewFor(match.uuid)}
                  busy={busy}
                  canEdit={canEdit}
                  onDismiss={() =>
                    applyDecision(
                      match.uuid,
                      reviewFor(match.uuid) === REVIEW_DECISION.DISMISS ? null : REVIEW_DECISION.DISMISS
                    )
                  }
                  onMatch={() =>
                    applyDecision(
                      match.uuid,
                      reviewFor(match.uuid) === REVIEW_DECISION.MATCH ? null : REVIEW_DECISION.MATCH
                    )
                  }
                />
              ))}
              {expanded &&
                dismissed.map((match) => (
                  <MatchCard
                    key={match.uuid}
                    match={match}
                    decision={reviewFor(match.uuid)}
                    busy={busy}
                    canEdit={canEdit}
                    onDismiss={() =>
                      applyDecision(
                        match.uuid,
                        reviewFor(match.uuid) === REVIEW_DECISION.DISMISS ? null : REVIEW_DECISION.DISMISS
                      )
                    }
                    onMatch={() =>
                      applyDecision(
                        match.uuid,
                        reviewFor(match.uuid) === REVIEW_DECISION.MATCH ? null : REVIEW_DECISION.MATCH
                      )
                    }
                  />
                ))}
            </Cards>
          </BucketSection>
        );
      })}
    </>
  );
}

// A single sex-offender candidate. Renders the mockup layout: photo, header (name + badges +
// dismiss/match), a 6-col demographics grid, address, crimes (structured from
// `stateData.offenses[]` falling back to the singular `crime` string), and sourced registries
// (links from `sources[]`). Every field renders "–" when the API sent nothing — the mockup treats
// missing fields as visible gaps, not hidden.
function MatchCard({
  match,
  decision,
  busy,
  canEdit,
  onDismiss,
  onMatch,
}: {
  match: OffenderMatch;
  decision: ReviewDecision | undefined;
  busy: boolean;
  canEdit: boolean;
  onDismiss: () => void;
  onMatch: () => void;
}) {
  const isMatched = decision === REVIEW_DECISION.MATCH;
  const isDismissed = decision === REVIEW_DECISION.DISMISS;
  const displayName = fullName(match);
  const crimes = matchCrimes(match);
  const sources = matchSources(match);

  return (
    <Card data-testid='sex-offender-match-card' $matched={isMatched} $dismissed={isDismissed}>
      <Photo match={match} />
      <CardBody>
        <CardHeader>
          <NameAndBadges>
            <MatchName>{displayName}</MatchName>
          </NameAndBadges>
          {/* The card still reads out its state to a viewer: a matched card keeps its blue border
              and a dismissed one its greyed opacity, so what someone decided stays legible without
              the toggles that would change it. */}
          <Actions>
            {canEdit && (
              <>
                {/* Dismiss and Match are toggles — clicking a set state clears it. Both disabled while
                a mutation is in flight (busy). Both stay neutral outline regardless of state — the
                card's blue border (isMatched) and greyed opacity (isDismissed) carry the visual
                signal, and the label swap reads out the state in copy. AdjudicationButton tightens
                the shared Button's default padding + weight to match the mockup pill shape (same
                trick FiltersButton in InboundReferralsListPage uses for its compact pill). */}
                <AdjudicationButton
                  type='button'
                  variant='outline-gray'
                  onClick={onDismiss}
                  disabled={busy || isMatched}>
                  <IconGlyph aria-hidden>✕</IconGlyph>
                  {isDismissed ? 'Dismissed' : 'Dismiss'}
                </AdjudicationButton>
                <AdjudicationButton
                  type='button'
                  variant='outline-gray'
                  onClick={onMatch}
                  disabled={busy || isDismissed}>
                  <MatchCheckIcon aria-hidden />
                  {isMatched ? 'Remove Match' : 'Match'}
                </AdjudicationButton>
              </>
            )}
          </Actions>
        </CardHeader>

        <DemographicsGrid>
          <Field label='Sex' value={match.sex} />
          <Field label='DOB' value={simpleDate(match.dob) || '–'} />
          <Field label='Age' value={match.age} />
          <Field label='Race' value={match.race} />
          <Field label='Hair Color' value={match.hairColor} />
          <Field label='Eye Color' value={match.eyeColor} />
        </DemographicsGrid>

        <Field label='Address' value={match.address} block />

        <CrimesSection>
          <FieldLabel>Crimes</FieldLabel>
          {crimes.length === 0 ? (
            <FieldValue>No offense details available</FieldValue>
          ) : (
            <CrimesList>
              {crimes.map((line, i) => (
                <li key={`${line.offense}-${i}`}>
                  <CrimeOffense>{line.offense}</CrimeOffense>
                  {line.meta && <CrimeMeta>{line.meta}</CrimeMeta>}
                </li>
              ))}
            </CrimesList>
          )}
        </CrimesSection>

        {sources.length > 0 && (
          <SourcesSection>
            <FieldLabel>Sourced Registries:</FieldLabel>
            <SourcesList>
              {sources.map((s, i) => (
                <SourceItem key={`${s.name}-${i}`}>
                  <SourceLink href={s.url ?? '#'} target='_blank' rel='noreferrer noopener'>
                    {s.name}
                    <LinkIcon width={10} height={10} color={colors.blue100} />
                  </SourceLink>
                </SourceItem>
              ))}
            </SourcesList>
          </SourcesSection>
        )}
      </CardBody>
    </Card>
  );
}

// The photo cell. `offenderImageUrlR2` (Olio-hosted mirror) is preferred over `offenderImageUrl`
// (state registry direct URL, unreliable / CORS-hostile). Missing URL OR a URL that fails to
// resolve (404, CORS, network) both fall back to a bundled placeholder image.
function Photo({ match }: { match: OffenderMatch }) {
  const src = match.offenderImageUrlR2 || match.offenderImageUrl || fallbackImageUrl;
  const [failed, setFailed] = useState(false);
  const displayed = failed ? fallbackImageUrl : src;
  return (
    <PhotoCell>
      <PhotoImg src={displayed} alt='' loading='lazy' onError={() => setFailed(true)} />
    </PhotoCell>
  );
}

// A one-line field label + value block, used across the demographics grid and the address row.
// A missing value renders as "–" so gaps stay visible (matches the mockup).
function Field({ label, value, block }: { label: string; value?: string | null; block?: boolean }) {
  return (
    <FieldBlock $block={block}>
      <FieldLabel>{label}</FieldLabel>
      <FieldValue>{value || '–'}</FieldValue>
    </FieldBlock>
  );
}

function fullName(match: OffenderMatch): string {
  if (match.name) return match.name;
  return [match.firstName, match.middleName, match.lastName].filter(Boolean).join(' ') || '–';
}

// Title-case a filter value for display. Short all-uppercase tokens (≤ 2 chars — state codes
// like "WA", sex "M"/"F") stay as-is so we don't mangle them into "Wa".
function displayFilterValue(v: string): string {
  if (v.length <= 2 && v === v.toUpperCase()) return v;
  return v.toLowerCase().replace(/\b[a-z]/g, (c) => c.toUpperCase());
}

interface CrimeLine {
  offense: string;
  meta?: string;
}

// Per-crime lines the FE renders as a bullet list. Real offenders.io data is far messier than
// the schema suggests, so this is deliberately defensive:
//   - Prefer stateData.offenses[] (extensive mode) and pull out `offense` + a formatted meta line
//     ("Feb 8, 2012 · Washington"). Drop the meta line entirely when both fields are missing.
//   - `offense` may itself contain embedded `\n` (VA registry aggregator squashes multiple
//     statutes + `Date Convicted:` blocks into a single string). The render uses
//     `white-space: pre-line`, so we pass the raw text through — parsing it would be fragile.
//   - When stateData.offenses is absent or empty, fall back to the top-level `crime` string and
//     split it on `\n` so multi-crime `crime` blobs (Booker Smith etc.) render as separate bullets
//     instead of one wall.
//   - Skip entries whose offense text ended up empty after trim (offenses: [] with placeholder
//     objects, or a bare `\n` from the crime fallback).
function matchCrimes(match: OffenderMatch): CrimeLine[] {
  const offenses: OffenderOffense[] | undefined = match.stateData?.offenses;
  if (Array.isArray(offenses) && offenses.length > 0) {
    return offenses
      .map((o) => {
        const offense = (o.offense ?? '').trim();
        const date = simpleDate(o.convictionDate ?? undefined) || (o.convictionDate ?? '').trim();
        const meta = [date, (o.jurisdiction ?? '').trim()].filter(Boolean).join(' · ') || undefined;
        return { offense, meta };
      })
      .filter((line) => line.offense.length > 0);
  }
  return (match.crime ?? '')
    .split('\n')
    .map((line) => ({ offense: line.trim() }))
    .filter((line) => line.offense.length > 0);
}

// The named external-link entries next to `Sourced Registries:`. offenders.io returns each source
// as `{id, name, url}`; we drop entries without a URL (can't link) and de-null the display name.
function matchSources(match: OffenderMatch): { name: string; url: string | null }[] {
  const sources: OffenderSource[] | undefined = match.sources;
  if (!Array.isArray(sources)) return [];
  return sources.filter((s) => !!s.url).map((s) => ({ name: s.name ?? 'Source', url: s.url ?? null }));
}

const SummaryText = styled(BodySmall)`
  color: ${colors.black50};
  font-size: 12px;
`;

const MatchesHeader = styled(BodySmallBold)`
  color: ${colors.black100};
  font-size: 12px;
`;

const ListHeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
`;

// Sits between PanelHeader and PanelBody as a flex sibling of both. Panel is `flex-direction:
// column; height: 100%`, PanelBody grows via `flex: 1` and owns the overflow, so FilterBar
// stays visually fixed at the top of the content region without needing `position: sticky`.
// Padding + bottom divider mirror PanelHeader's chrome so the two look like a single stack.
// Padding and the rule below come from the shell's detailsBar slot, which is what keeps this strip
// pinned above the scrolling body.
const FilterBar = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FiltersRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 8px;
`;

const DismissedToggleRow = styled.div`
  display: flex;
  justify-content: center;
`;

const FilterCell = styled.div`
  display: flex;
  width: 100%;
`;

const NoResultsRow = styled.div`
  padding: 24px;
  font-size: 13px;
  color: ${colors.black50};
  text-align: center;
  border: 1px dashed ${colors.black10};
  border-radius: 4px;
`;

// Bucket section — header row ("2 'Strong' Matches" + "Dismiss All") on top, an optional
// dismissed-toggle button beneath it, then the visible cards. Sections stack vertically inside
// PanelBody with a gap between them (inherited from PanelBody's flex layout).
const BucketSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const BucketHeader = styled.div`
  display: flex;
  align-items: baseline;
  gap: 12px;
`;

const BucketHeaderLabel = styled(BodySmallBold)`
  color: ${colors.black100};
  font-size: 12px;
`;

// Blue underline text link (not a button pill). Only rendered when the section has anything to
// dismiss — see MatchCards. Disabled state kills the pointer + dims the text so it still reads
// as a link but doesn't invite a double-click while a save is in flight.
const DismissAllLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  font-size: 13px;
  color: ${colors.blue100};
  text-decoration: underline;
  cursor: pointer;

  &:disabled {
    color: ${colors.black50};
    cursor: default;
  }
`;

const Cards = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Card = styled.div<{ $matched: boolean; $dismissed: boolean }>`
  display: grid;
  grid-template-columns: 148px 1fr;
  gap: 16px;
  padding: 16px;
  border: 1px solid ${colors.black10};
  border-radius: 6px;
  background: rgba(9, 15, 37, 0.01);
  ${({ $matched }) =>
    $matched &&
    css`
      border-color: ${colors.blue100};
      background: ${colors.blue5};
    `}
  ${({ $dismissed }) =>
    $dismissed &&
    css`
      opacity: 0.55;
    `}
`;

const PhotoCell = styled.div`
  width: 148px;
  height: 148px;
`;

const PhotoImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 4px;
`;

const CardBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 0;
`;

// Actions (Dismiss/Match) must always sit in the top-right corner. Dropping flex-wrap on the
// header prevents the whole Actions block from wrapping below when NameAndBadges grows wide.
// NameAndBadges takes the remaining space (flex: 1) and wraps its OWN children instead — so a
// long name + bucket chip just adds vertical height on the left, while Match/Dismiss stay right.
const CardHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
`;

const NameAndBadges = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  flex: 1;
  min-width: 0;
`;

const MatchName = styled(BodySmallBold)`
  font-size: 15px;
  color: ${colors.black100};
`;

const Actions = styled.div`
  display: flex;
  gap: 8px;
  flex-shrink: 0;
`;

// Tightens the shared Button for the per-match adjudication row. Figma spec (9749:4716):
// 24px height, 12px Lato Regular, 4px radius, rgba(2,19,32,0.1) border (lighter than the
// filter pill's border), rgba(2,19,32,0.75) text. Same styled(Button) pattern FiltersButton
// uses in InboundReferralsListPage to override the shared Button's chunkier defaults.
const AdjudicationButton = styled(Button)`
  height: 24px;
  padding: 0 10px;
  font-size: 12px;
  font-weight: normal;
  line-height: 14px;
  letter-spacing: 0.18px;
  gap: 6px;
  border-radius: 4px;
  border-color: ${colors.black10};
  color: ${colors.black75};
`;

const IconGlyph = styled.span`
  display: inline-flex;
  font-size: 12px;
  line-height: 1;
`;

// Two-tone pill label ("State:" at 50% opacity, "All" at 100%) — matches the Figma spec
// (9749:4646) for the collapsed filter pill's inner text. Kept inline instead of a shared
// component because it's a single-file rendering choice for the filter/sort formatOptionLabel.
function twoTonePillLabel(prefix: string, value: string) {
  return (
    <>
      <PillPrefix>{prefix}: </PillPrefix>
      <PillValue>{value}</PillValue>
    </>
  );
}

const PillPrefix = styled.span`
  color: ${colors.black50};
`;

const PillValue = styled.span`
  color: ${colors.black100};
`;

// react-select style overrides that stamp the shared Select onto the Figma filter-pill spec
// (9749:4645): 32px height, 12px Lato Regular, 4px radius, black25 border, 12/16 horizontal
// padding, black75 caret. Merged onto react-select's built-in defaults per style key (using
// `...provided`) so anything we don't touch keeps its stock behavior.
const FILTER_PILL_STYLES = {
  container: (provided: Record<string, unknown>) => ({
    ...provided,
    height: 32,
    width: '100%',
  }),
  control: (provided: Record<string, unknown>, state: { isFocused: boolean }) => ({
    ...provided,
    minHeight: 32,
    height: 32,
    width: '100%',
    fontSize: 12,
    lineHeight: '12px',
    borderRadius: 4,
    borderColor: state.isFocused ? colors.blue100 : colors.black25,
    boxShadow: 'none',
    cursor: 'pointer',
    '&:hover': { borderColor: colors.blue100 },
  }),
  valueContainer: (provided: Record<string, unknown>) => ({
    ...provided,
    height: 32,
    padding: '0 4px 0 12px',
  }),
  singleValue: (provided: Record<string, unknown>) => ({
    ...provided,
    fontSize: 12,
    letterSpacing: '0.18px',
    margin: 0,
  }),
  input: (provided: Record<string, unknown>) => ({
    ...provided,
    margin: 0,
    padding: 0,
  }),
  indicatorSeparator: () => ({ display: 'none' }),
  indicatorsContainer: (provided: Record<string, unknown>) => ({
    ...provided,
    height: 32,
  }),
  dropdownIndicator: (provided: Record<string, unknown>) => ({
    ...provided,
    padding: '0 12px 0 4px',
    color: colors.black75,
    '& svg': { width: 12, height: 12 },
  }),
  menu: (provided: Record<string, unknown>) => ({
    ...provided,
    fontSize: 12,
    borderRadius: 4,
    zIndex: 100,
  }),
  option: (provided: Record<string, unknown>) => ({
    ...provided,
    fontSize: 12,
    padding: '8px 12px',
  }),
};

// The "View Dismissed Result" toggle (Figma 9749:4782). Primary-blue border, black75 text,
// 24px height — different color balance from AdjudicationButton (which uses the lighter
// border-lighter variable). Kept as a styled(Button) so we inherit all the shared Button's
// state handling.
const ViewDismissedButton = styled(Button)`
  height: 24px;
  padding: 0 12px;
  font-size: 12px;
  font-weight: normal;
  line-height: 14px;
  letter-spacing: 0.18px;
  border-radius: 4px;
  border-color: ${colors.blue100};
  color: ${colors.black75};
  background: ${colors.white};
`;

const DemographicsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: 12px 16px;
`;

const FieldBlock = styled.div<{ $block?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 4px;
  ${({ $block }) =>
    $block &&
    css`
      grid-column: 1 / -1;
    `}
`;

const FieldLabel = styled.div`
  font-size: 12px;
  color: ${colors.black50};
`;

const FieldValue = styled.div`
  font-size: 13px;
  color: ${colors.black100};
`;

const CrimesSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const CrimesList = styled.ul`
  margin: 0;
  padding-left: 18px;
  color: ${colors.black100};
  font-size: 13px;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

// pre-line preserves embedded newlines from unnormalized registry data (VA aggregator dumps
// multiple statutes + Date Convicted/Court/Case Number rows into a single string). Without this,
// those `\n`s collapse and the offense reads as one long horizontal wall.
const CrimeOffense = styled.div`
  white-space: pre-line;
`;

const CrimeMeta = styled.div`
  font-size: 11px;
  color: ${colors.black50};
  margin-top: 2px;
`;

const SourcesSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const SourcesList = styled.ul`
  margin: 0;
  padding-left: 18px;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const SourceItem = styled.li`
  font-size: 13px;
  color: ${colors.blue100};
`;

const SourceLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: ${colors.blue100};
  text-decoration: underline;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 64px 32px;
  text-align: center;
  border-radius: 8px;
  background: ${colors.green10};
`;

const EmptyTitle = styled(BodySmallBold)`
  font-size: 16px;
  color: ${colors.black100};
`;

const EmptyBody = styled(BodySmall)`
  color: ${colors.black75};
  max-width: 420px;
`;
